import {
  Component,
  Host,
  h,
  Prop,
  Element,
  Event,
  State,
  EventEmitter,
  getAssetPath,
  Method,
  Fragment,
  JSX
} from "@stencil/core";

import { Locale } from "../../../common/locale";
import {
  ModuleServerType,
  ModuleServerFullData,
  EditServerModuleResult
} from "../types";
import { getIconPath, MercuryBundles } from "@genexus/mercury";
import { ComboBoxItemModel } from "@genexus/chameleon-controls-library/dist/types/components/combo-box/types";
import { mapTypesToComboboxItemModel } from "./helpers";

const CSS_BUNDLES: MercuryBundles = [
  "resets/box-sizing",
  "utils/form--full",
  "utils/layout",
  "utils/typography",
  "utils/spacing",
  "chameleon/scrollbar"
];

const MORE_ICON = getIconPath({
  category: "gemini-tools",
  name: "show-more-horizontal",
  colorType: "on-surface"
});

@Component({
  tag: "gx-ide-edit-module-server",
  styleUrl: "edit-module-server.scss",
  shadow: true,
  assetsDirs: ["gx-ide-assets/edit-module-server"]
})
export class GxIdeEditModuleServer {
  #_componentLocale: any;
  #renderedFirstTime = false;
  #serverTypesModel: ComboBoxItemModel[] = [];
  #shortcutsSrc = getAssetPath(
    `./gx-ide-assets/edit-module-server/shortcuts.json`
  );

  @Element() el: HTMLGxIdeEditModuleServerElement;
  #chShortcutsEl: HTMLChShortcutsElement;
  #nameEl: HTMLChEditElement;
  #passwordEl: HTMLChEditElement;
  #serverTypeEl: HTMLChComboBoxRenderElement;
  #sourceEl: HTMLChEditElement;
  #userEl: HTMLChEditElement;

  @State() selectedType: "local" | "remote" = "local";

  /**
   * Cancel callback
   */
  @Prop() readonly cancelCallback: CancelCallback;

  /**
   * Confirm callback
   */
  @Prop() readonly confirmCallback: ConfirmCallback;

  /**
   * The name of the server
   */
  @Prop() readonly name: string;

  /**
   * Callback invoked when wants to select a local folder that have modules inside
   */
  @Prop() readonly selectSourceCallback: SelectSourceCallback;

  /**
   * Disk path or server url
   */
  @Prop() readonly source: string;

  /**
   * Id of the currently selected server type
   */
  @Prop() readonly type: string;

  /**
   * The type of the server
   */
  @Prop() readonly types: ModuleServerType[];

  /**
   * This event is emitted once just after the component is fully loaded and the first render() occurs
   */
  @Event() componentDidRenderFirstTime: EventEmitter<boolean>;

  async componentWillLoad() {
    this.#_componentLocale = await Locale.getComponentStrings(this.el);
    this.#serverTypesModel = mapTypesToComboboxItemModel(this.types);
    if (this.types && this.types.length > 0) {
      this.selectedType = this.types[0].type as "local" | "remote";
    }
  }

  componentDidRender() {
    if (!this.#renderedFirstTime) {
      this.componentDidRenderFirstTime.emit(
        this.#_componentLocale.componentName
      );
      this.#renderedFirstTime = true;
    }
  }

  /**
   * Suspends or reactivates the shortcuts
   */
  @Method()
  async suspendShortcuts(suspendShortcuts: boolean) {
    if (suspendShortcuts) {
      this.#chShortcutsEl.suspend = true;
    } else {
      this.#chShortcutsEl.suspend = false;
    }
  }

  #cancelCallbackHandler = async () => {
    await this.cancelCallback();
  };

  #confirmCallbackHandler = async () => {
    if (this.confirmCallback) {
      const selectedTypeId = this.types.find(
        ({ type }) => type === this.selectedType
      );
      let data = {} as ModuleServerFullData;
      if (selectedTypeId.type === "remote") {
        data = {
          id: selectedTypeId.id,
          name: this.#nameEl.value,
          type: this.#serverTypeEl.value as "remote" | "local",
          authenticated: selectedTypeId.authenticated,
          source: this.#sourceEl.value,
          user: this.#userEl.value,
          password: this.#passwordEl.value
        };
      } else {
        data = {
          id: selectedTypeId.id,
          name: this.#nameEl.value,
          type: this.#serverTypeEl.value as "remote" | "local",
          authenticated: selectedTypeId.authenticated,
          source: this.#sourceEl.value
        };
      }
      const result = await this.confirmCallback(data);
      if (result.success) {
        // display success message
      } else {
        // display error message
      }
    }
  };

  #handleServerTypeChange = (e: CustomEvent<string>) => {
    this.selectedType = e.detail as "local" | "remote";
  };

  #renderFooter = () => {
    return (
      <footer class="footer dialog-footer dialog-footer-with-border spacing-body-block-end spacing-body-inline">
        <div class="buttons-spacer">
          <button
            class="button-secondary"
            id="button-cancel"
            onClick={this.#cancelCallbackHandler}
            part="button-cancel"
          >
            {this.#_componentLocale.footer.btnCancel}
          </button>
          <button
            class="button-primary"
            id="button-create"
            onClick={this.#confirmCallbackHandler}
            part="button-create"
          >
            {this.#_componentLocale.footer.btnConfirm}
          </button>
        </div>
      </footer>
    );
  };

  #renderServerOptions = (): JSX.Element[] => {
    return (
      <Fragment>
        <label class="label" htmlFor="user">
          {this.#_componentLocale.user}
        </label>
        <ch-edit
          id="user"
          part="user"
          class="input"
          placeholder={this.#_componentLocale.user}
          ref={(el: HTMLChEditElement) =>
            (this.#userEl = el as HTMLChEditElement)
          }
        ></ch-edit>

        <label class="label" htmlFor="password">
          {this.#_componentLocale.password}
        </label>
        <ch-edit
          id="password"
          part="password"
          class="input"
          placeholder={this.#_componentLocale.password}
          ref={(el: HTMLChEditElement) =>
            (this.#passwordEl = el as HTMLChEditElement)
          }
        ></ch-edit>
      </Fragment>
    );
  };

  #selectSourceCallbackHandle = async () => {
    if (this.selectSourceCallback) {
      const path = await this.selectSourceCallback();

      if (path) {
        this.#sourceEl.value = path;
      }
    }
  };

  render() {
    const renderLocalSourceButton = this.selectedType === "local";
    const sourceChEdit = (
      <ch-edit
        placeholder={this.#_componentLocale.source}
        ref={(el: HTMLChEditElement) =>
          (this.#sourceEl = el as HTMLChEditElement)
        }
        value={this.source}
        class="input"
        part="source"
        id="source"
      ></ch-edit>
    );
    const sourceButton = (
      <button
        class="button-tertiary button-icon-only"
        aria-label="caption"
        onClick={this.#selectSourceCallbackHandle}
      >
        <ch-image class="icon-m" src={MORE_ICON}></ch-image>
      </button>
    );

    return (
      <Host class="widget">
        <ch-theme model={CSS_BUNDLES}></ch-theme>
        <ch-shortcuts
          src={this.#shortcutsSrc}
          ref={(el: HTMLChShortcutsElement) =>
            (this.#chShortcutsEl = el as HTMLChShortcutsElement)
          }
        ></ch-shortcuts>

        <section class="section">
          <main class="main field-group-justified-start">
            <label class="label" htmlFor="server-type">
              {this.#_componentLocale.type}
            </label>
            <ch-combo-box-render
              accessibleName="server types"
              id="server-type"
              part="server-type"
              class="combo-box"
              model={this.#serverTypesModel}
              value={this.selectedType}
              onChange={this.#handleServerTypeChange}
              ref={(el: HTMLChComboBoxRenderElement) =>
                (this.#serverTypeEl = el as HTMLChComboBoxRenderElement)
              }
            ></ch-combo-box-render>

            <label class="label" htmlFor="name">
              {this.#_componentLocale.name}
            </label>
            <ch-edit
              placeholder={this.#_componentLocale.name}
              ref={(el: HTMLChEditElement) =>
                (this.#nameEl = el as HTMLChEditElement)
              }
              value={this.name}
              part="name"
              class="input"
              id="name"
            ></ch-edit>

            <label class="label" htmlFor="source">
              {this.#_componentLocale.source}
            </label>

            {renderLocalSourceButton ? (
              <div class="source-container">{[sourceChEdit, sourceButton]}</div>
            ) : (
              sourceChEdit
            )}

            {this.selectedType === "remote" && this.#renderServerOptions()}
          </main>

          {this.#renderFooter()}
        </section>
      </Host>
    );
  }
}
export type ConfirmCallback = (
  data: ModuleServerFullData
) => Promise<EditServerModuleResult>;
export type CancelCallback = () => Promise<void>;
export type SelectSourceCallback = () => Promise<string | undefined>;
